/**
 * Feature flags in the panels (ARCHITECT §18.1 step 4). The panel loads its resolved values once
 * (GET /api/v1/<panel>/features; the vendor panel gets values already resolved for its vendor):
 *
 *   const reviewsOn = useFeature("reviews.enabled");             // boolean flags
 *   const mode = useFeatureValue("storefront.render.product");   // choice flags → "ssr"
 *   <Feature flag="support.tickets" fallback={null}>…</Feature>
 *
 * Module manifests can also put `feature: "<key>"` on routes and nav items. Hiding UI is cosmetic:
 * the API refuses switched-off features anyway (404 feature_disabled).
 */

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import {
  adminFeaturesRetrieve,
  storeFeaturesRetrieve,
  vendorFeaturesRetrieve,
  type ApiError,
} from "@motolynk/api-client";

import type { Panel } from "./module";
import { usePanel } from "./session";

export type FeatureValues = Record<string, boolean | string>;

export const featuresKey = (panel: Panel) => ["features", panel] as const;

const fetchers = {
  admin: adminFeaturesRetrieve,
  vendor: vendorFeaturesRetrieve,
  store: storeFeaturesRetrieve,
};

export function usePanelFeatures(panel: Panel, enabled = true) {
  return useQuery<FeatureValues, ApiError>({
    queryKey: featuresKey(panel),
    queryFn: async () => (await fetchers[panel]()).data as FeatureValues,
    enabled,
    staleTime: 60_000,
  });
}

/** True when a boolean flag is on. Unknown flags (e.g. a module that isn't installed) are off. */
export function isFeatureOn(features: FeatureValues, key: string | undefined): boolean {
  return key === undefined || features[key] === true;
}

export function useFeature(key: string): boolean {
  return isFeatureOn(usePanel().features, key);
}

export function useFeatureValue(key: string): string | boolean | undefined {
  return usePanel().features[key];
}

export interface FeatureProps {
  flag: string;
  /** For choice flags: render only when the flag has this value. */
  value?: string;
  /** Rendered while the feature is off (default: nothing). */
  fallback?: ReactNode;
  children: ReactNode;
}

export function Feature({ flag, value, fallback = null, children }: FeatureProps) {
  const { features } = usePanel();
  const on = value === undefined ? isFeatureOn(features, flag) : features[flag] === value;
  return <>{on ? children : fallback}</>;
}
